import React from 'react';
import { CheckIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatPhone } from '../../utils/format';
import { siteConfig } from '../../data/siteConfig';
import type { StepErrors } from '../../utils/validation';
import type { ContactChannel } from '../../types/funnel';
import { Segmented } from './Segmented';
import { StepHeading } from './StepHeading';
import { TextField } from './TextField';
import { HelpAnswers } from './HelpAnswers';
function ConsentBox({
  checked,
  onChange,
  label,
  tag,
  error
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tag: string;
  error?: string;
}) {
  return <div data-id="element-673" data-mp="3qzjklq75c5i4">
      <label className="flex cursor-pointer items-start gap-3 py-2" data-id="element-674" data-mp="3uyr89jbom1so">
        <input type="checkbox" className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)} aria-invalid={!!error} data-id="element-675" data-mp="3uxzhclt14s8q" />
        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-[background-color,border-color] duration-150 ease-out peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-magenta ${checked ? 'border-ink bg-ink text-lime' : error ? 'border-magenta bg-white' : 'border-ink-300 bg-white'}`} aria-hidden="true" data-id="element-676" data-mp="8r14bdqmn4lk">
          {checked && <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-677" data-mp="203kdaqp70330" />}
        </span>
        <span className="text-[15px] leading-relaxed text-ink" data-id="element-678" data-mp="205n59g9wo6ad">
          {label} <span className="text-sm font-semibold text-ink-500" data-id="element-679" data-mp="1w2qs4534x2dt">({tag})</span>
        </span>
      </label>
      {error && <p className="ml-9 text-sm font-medium text-magenta-dark" data-id="element-680" data-mp="1r9e5g0i0n5np">{error}</p>}
    </div>;
}
export function StepContact({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    data,
    update
  } = useFunnel();
  const err = (k: string) => errors[k] ? t(errors[k]!) : undefined;
  return <div data-id="element-681" data-mp="2ezia8b6xxbga">
      <StepHeading title={t('contactTitle')} data-id="element-682" data-mp="2ezrwbnov6ygp" />
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-683" data-mp="2xwtkt4c9h5vm">
        <TextField label={t('firstName')} autoComplete="given-name" value={data.firstName} onChange={v => update({
        firstName: v
      })} error={err('firstName')} data-id="element-684" data-mp="2xy3hbp3gu605" />
        <TextField label={t('lastName')} autoComplete="family-name" value={data.lastName} onChange={v => update({
        lastName: v
      })} error={err('lastName')} data-id="element-685" data-mp="2p3ymkul7oafc" />
        <TextField label={t('phone')} type="tel" inputMode="tel" autoComplete="tel-national" value={data.phone} onChange={v => update({
        phone: formatPhone(v)
      })} error={err('phone')} data-id="element-686" data-mp="2t48uwdwh7cxd" />
        <TextField label={t('email')} type="email" autoComplete="email" value={data.email} onChange={v => update({
        email: v
      })} error={err('email')} data-id="element-687" data-mp="2k3u4j04nn7n0" />
      </div>
      <div className="mt-6" data-id="element-688" data-mp="2a8in2a1v3mbk">
        <Segmented<ContactChannel> label={t('channelLabel')} value={data.contactChannel} onChange={v => update({
        contactChannel: v
      })} options={[{
        value: 'call',
        label: t('chCall')
      }, {
        value: 'text',
        label: t('chText')
      }, {
        value: 'email',
        label: t('chEmail')
      }]} data-id="element-689" data-mp="2a9trmc34sy3q" />
      </div>
      <div className="mt-8 space-y-1 border-t border-ink-200 pt-6" data-id="element-690" data-mp="1oyee5v4qyivl">
        <ConsentBox checked={data.consentQuote} onChange={v => update({
        consentQuote: v
      })} label={t('consentQuote')} tag={t('consentRequired')} error={err('consentQuote')} data-id="element-691" data-mp="22ohu64d19gcl" />
        <ConsentBox checked={data.consentSms} onChange={v => update({
        consentSms: v
      })} label={t('consentSms')} tag={t('consentOptional')} data-id="element-692" data-mp="2cme5ox9vudb8" />
        <ConsentBox checked={data.consentAiCall} onChange={v => update({
        consentAiCall: v
      })} label={t('consentAi')} tag={t('consentOptional')} data-id="element-693" data-mp="27mjawimzl3kw" />
        {(siteConfig.privacyUrl || siteConfig.smsTermsUrl) && <p className="pl-9 pt-1 text-sm text-ink-600" data-id="element-694" data-mp="2goixdqgrszv8">
            {siteConfig.privacyUrl && <a href={siteConfig.privacyUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2" data-id="element-695" data-mp="r9kzoey2xyxu">
                Privacy Policy
              </a>}
            {siteConfig.privacyUrl && siteConfig.smsTermsUrl && ' · '}
            {siteConfig.smsTermsUrl && <a href={siteConfig.smsTermsUrl} target="_blank" rel="noreferrer" className="underline underline-offset-2" data-id="element-696" data-mp="songwnqfwy18">
                SMS Terms
              </a>}
          </p>}
      </div>
      <HelpAnswers screen="stepContact" className="mt-10" data-id="element-697" data-mp="12mboie95a00t" />
    </div>;
}